import { useCallback, useEffect, useRef, useState } from "react";
import type { Sale, SalesMode, SalesProvider } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";

export interface SalesChannel {
  items: Sale[];
  status: "loading" | "ready" | "not_configured" | "error" | "paused";
  cursor: string | null;
  message: string | null;
  fetchedAt: string | null;
}
const empty = (): SalesChannel => ({ items: [], status: "loading", cursor: null, message: null, fetchedAt: null });
const emptyChannels = () => ({ stripe: empty(), square: empty() });

export function useSalesData(mode: SalesMode, from: string, to: string, refresh: number) {
  const api = useApi();
  const generation = useRef(0);
  const channelRef = useRef(emptyChannels());
  const key = JSON.stringify([mode, from, to, refresh]);
  const emptyView = useRef(emptyChannels());
  const [report, setReport] = useState(() => ({ key: "", channels: emptyChannels() }));
  const loaderRef = useRef<(provider: SalesProvider) => void>(() => {});

  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    channelRef.current = emptyChannels(); setReport({ key, channels: channelRef.current });
    const active = () => current === generation.current && !controller.signal.aborted;
    function publish(provider: SalesProvider, next: SalesChannel) {
      if (!active()) return;
      channelRef.current = { ...channelRef.current, [provider]: next }; setReport({ key, channels: channelRef.current });
    }
    async function load(provider: SalesProvider) {
      const previous = channelRef.current[provider];
      let cursor = previous.cursor;
      const payments = new Map(previous.items.map(sale => [sale.id, sale]));
      let fetchedAt = previous.fetchedAt;
      publish(provider, { ...previous, status: "loading", message: null });
      try {
        // Bound each batch to keep very large histories manageable. Further
        // batches are explicit; partial totals are always labelled in the UI.
        const seen = new Set<string>();
        for (let page = 0; page < 20 && active(); page++) {
          const result = await api.getSalesPage({ provider, mode, from, to, cursor: cursor ?? undefined }, controller.signal);
          if (!active()) return;
          if (result.provider !== provider || result.mode !== mode) throw new Error("The report environment changed. Refresh sales.");
          if (result.status !== "ready") {
            publish(provider, { items: [...payments.values()], status: result.status, cursor, message: result.message, fetchedAt });
            return;
          }
          for (const sale of result.items) payments.set(sale.id, sale);
          fetchedAt = result.fetchedAt;
          const next = result.nextCursor;
          if (next && (seen.has(next) || next === cursor)) throw new Error("Sales history did not advance. Refresh and retry.");
          if (next) seen.add(next);
          cursor = next;
          publish(provider, { items: [...payments.values()], cursor, status: cursor ? "loading" : "ready", message: null, fetchedAt });
          if (!cursor) return;
        }
        if (active()) publish(provider, { items: [...payments.values()], cursor, status: "paused", message: "More sales are available. Load the next batch to complete the totals.", fetchedAt });
      } catch (error) {
        if (active()) publish(provider, { items: [...payments.values()], cursor, status: "error", message: error instanceof Error ? error.message : "Could not load sales.", fetchedAt });
      }
    }
    loaderRef.current = provider => { if (channelRef.current[provider].status !== "loading") void load(provider); };
    void load("stripe"); void load("square");
    return () => { controller.abort(); generation.current++; };
  }, [api, mode, from, to, refresh]);

  const loadMore = useCallback((provider: SalesProvider) => loaderRef.current(provider), []);
  return { channels: report.key === key ? report.channels : emptyView.current, loadMore };
}
